import { useEffect, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCountryName } from './countryCodeMapping';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -----------------------
   Condition options
----------------------- */
const CONDITION_OPTIONS = [
	{ value: 'is', label: 'is' },
	{ value: 'is_not', label: 'is not' }
];

const LocationFilterModal = ({
	isOpen,
	onClose,
	onApply,
	websiteId,
	token,
	analytics
}) => {
	/* -----------------------
	   Conditions
	----------------------- */
	const [countryCondition, setCountryCondition] = useState('is');
	const [regionCondition, setRegionCondition] = useState('is');
	const [cityCondition, setCityCondition] = useState('is');

	/* -----------------------
	   Multi-select values (ARRAYS)
	----------------------- */
	const [country, setCountry] = useState([]);
	const [region, setRegion] = useState([]);
	const [city, setCity] = useState([]);

	/* -----------------------
	   Options
	----------------------- */
	const [countries, setCountries] = useState([]);
	const [regions, setRegions] = useState([]);
	const [cities, setCities] = useState([]);

	// Auth header — token is null in publicMode; backend /available-locations
	// accepts unauthenticated reads on is_public sites.
	const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

	/* ------------------------------------
	   Seed options instantly from the dashboard's already-loaded analytics
	   so the dropdowns aren't empty during the first network round-trip.
	   We then immediately fetch /available-locations for the full distinct
	   lists (top-N from analytics is usually only ~10 items per category).
	   ------------------------------------ */
	useEffect(() => {
		if (!isOpen) return;

		// 1) Instant seed from dashboard analytics
		if (analytics) {
			setCountries(
				analytics.countries
					? Object.keys(analytics.countries).map(code => ({
						label: getCountryName(code),
						value: code
					}))
					: []
			);
			setRegions(
				analytics.regions
					? Object.keys(analytics.regions).map(r => ({ label: r, value: r }))
					: []
			);
			setCities(
				analytics.cities
					? Object.keys(analytics.cities).map(c => ({ label: c, value: c }))
					: []
			);
		}

		// 2) Fetch the FULL distinct lists (no country filter on initial open)
		if (!websiteId) return;
		const abort = new AbortController();
		(async () => {
			try {
				const res = await axios.get(
					`${API}/analytics/${websiteId}/available-locations`,
					{ headers: authHeaders, signal: abort.signal }
				);
				const data = res.data || {};
				setCountries(
					(data.countries || []).map(code => ({
						label: getCountryName(code) || code,
						value: code
					}))
				);
				setRegions((data.regions || []).map(r => ({ label: r, value: r })));
				setCities((data.cities || []).map(c => ({ label: c, value: c })));
			} catch (err) {
				if (axios.isCancel(err)) return;
				// Non-fatal: the seeded analytics-based lists still work.
				console.error('LocationFilterModal: failed to load available locations', err);
			}
		})();
		return () => abort.abort();
	}, [isOpen, analytics, websiteId, token]);

	/* ------------------------------------
	   Cascade: when the user picks one or more countries, narrow regions and
	   cities to ONLY those countries (Plausible-style geo filter UX). When
	   they clear the country selection, fall back to the full lists.
	   Region + city selections are also cleared on country change to avoid
	   leaving a stale selection that no longer exists in the narrowed lists.
	   ------------------------------------ */
	useEffect(() => {
		if (!isOpen || !websiteId) return;

		const abort = new AbortController();
		const countryParam = country.length
			? `?country=${encodeURIComponent(country.map(c => c.value).join('|'))}`
			: '';
		(async () => {
			try {
				const res = await axios.get(
					`${API}/analytics/${websiteId}/available-locations${countryParam}`,
					{ headers: authHeaders, signal: abort.signal }
				);
				const data = res.data || {};
				setRegions((data.regions || []).map(r => ({ label: r, value: r })));
				setCities((data.cities || []).map(c => ({ label: c, value: c })));
			} catch (err) {
				if (axios.isCancel(err)) return;
				console.error('LocationFilterModal: cascade fetch failed', err);
			}
		})();

		// Wipe region/city picks that may no longer be valid under the new country set.
		setRegion([]);
		setCity([]);
		return () => abort.abort();
		// Intentionally NOT depending on `analytics` here — only the country
		// selection should trigger the cascade.
	}, [country, isOpen, websiteId, token]);

	/* -----------------------
	   Apply filters (f grammar)
	----------------------- */
	const handleApply = () => {
		const fParams = [];

		if (country.length) {
			fParams.push(
				`${countryCondition},country,${country.map(c => c.value).join('|')}`
			);
		}

		if (region.length) {
			fParams.push(
				`${regionCondition},region,${region.map(r => r.value).join('|')}`
			);
		}

		if (city.length) {
			fParams.push(
				`${cityCondition},city,${city.map(c => c.value).join('|')}`
			);
		}

		if (fParams.length) {
			onApply(fParams);
		}

		onClose();
	};

	if (!isOpen) return null;

	return (
		<div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
			<div style={modalStyle}>
				{/* Header */}
				<div style={headerStyle}>
					<h3 style={titleStyle}>Filter by Location</h3>
					<button onClick={onClose} style={closeBtnStyle}>
						<X size={18} />
					</button>
				</div>

				{/* Country */}
				<FilterRow
					label="Country"
					condition={countryCondition}
					setCondition={setCountryCondition}
					value={country}
					setValue={setCountry}
					options={countries}
					isMulti
				/>

				{/* Region */}
				<FilterRow
					label="Region"
					condition={regionCondition}
					setCondition={setRegionCondition}
					value={region}
					setValue={setRegion}
					options={regions}
					isMulti
				/>

				{/* City */}
				<FilterRow
					label="City"
					condition={cityCondition}
					setCondition={setCityCondition}
					value={city}
					setValue={setCity}
					options={cities}
					isMulti
				/>

				{/* Footer */}
				<div style={footerStyle}>
					<Button
						onClick={handleApply}
						disabled={!country.length && !region.length && !city.length}
						style={{ backgroundColor: '#6b7280', color: '#fff' }}
					>
						Apply filter
					</Button>
				</div>
			</div>
		</div>
	);
};

/* -----------------------
   Reusable Filter Row
----------------------- */
const FilterRow = ({
	label,
	condition,
	setCondition,
	value,
	setValue,
	options,
	isMulti
}) => (
	<div style={{ marginBottom: '20px' }}>
		<label style={labelStyle}>{label}</label>

		<div style={{ display: 'flex', gap: '10px' }}>
			{/* Condition */}
			<select
				value={condition}
				onChange={e => setCondition(e.target.value)}
				style={selectStyle}
			>
				{CONDITION_OPTIONS.map(o => (
					<option key={o.value} value={o.value}>
						{o.label}
					</option>
				))}
			</select>

			{/* Value */}
			<div style={{ flex: 1 }}>
				<Select
					options={options}
					value={value}
					onChange={val => setValue(val || [])}
					isMulti={isMulti}
					isClearable
					isSearchable
					closeMenuOnSelect={false}
					placeholder={`Select ${label}`}
					noOptionsMessage={() => `No ${label.toLowerCase()}s found`}
					styles={reactSelectStyles}
				/>
			</div>
		</div>
	</div>
);

/* -----------------------
   Styles
----------------------- */
const overlayStyle = {
	position: 'fixed',
	inset: 0,
	backgroundColor: 'rgba(15, 23, 42, 0.45)',
	backdropFilter: 'blur(2px)',
	WebkitBackdropFilter: 'blur(2px)',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	zIndex: 9999
};

const modalStyle = {
	width: '460px',
	background: '#fff',
	borderRadius: '16px',
	padding: '24px',
	boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04)'
};

const headerStyle = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: '20px'
};

const titleStyle = {
	margin: 0,
	fontSize: '16px',
	fontWeight: 600,
	color: '#0f172a',
	letterSpacing: '-0.01em'
};

const footerStyle = {
	marginTop: '20px',
	paddingTop: '16px',
	borderTop: '1px solid #f1f5f9',
	display: 'flex',
	justifyContent: 'flex-end'
};

const closeBtnStyle = {
	background: 'transparent',
	border: 'none',
	cursor: 'pointer',
	color: '#64748b',
	width: 28,
	height: 28,
	borderRadius: 9999,
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	transition: 'background-color 0.15s, color 0.15s'
};

const labelStyle = {
	fontSize: '12px',
	fontWeight: 500,
	color: '#64748b',
	marginBottom: '8px',
	display: 'block'
};

const selectStyle = {
	padding: '10px 12px',
	borderRadius: '12px',
	border: '1px solid #e2e8f0',
	fontSize: '14px',
	background: '#fff',
	color: '#0f172a',
	minWidth: '120px',
	boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const reactSelectStyles = {
	control: (base, state) => ({
		...base,
		minHeight: 40,
		fontSize: 14,
		borderRadius: 12,
		borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
		boxShadow: state.isFocused
			? '0 0 0 3px rgba(99, 102, 241, 0.12)'
			: '0 1px 2px rgba(15, 23, 42, 0.04)',
		'&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
		transition: 'box-shadow 0.2s, border-color 0.2s'
	}),
	placeholder: (base) => ({ ...base, color: '#94a3b8' }),
	singleValue: (base) => ({ ...base, color: '#0f172a' }),
	indicatorSeparator: () => ({ display: 'none' }),
	dropdownIndicator: (base) => ({ ...base, color: '#94a3b8', padding: 6 }),
	menu: (base) => ({
		...base,
		borderRadius: 12,
		boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
		border: '1px solid #e2e8f0',
		overflow: 'hidden'
	}),
	option: (base, state) => ({
		...base,
		fontSize: 13,
		color: '#0f172a',
		backgroundColor: state.isSelected ? '#eef2ff' : state.isFocused ? '#f8fafc' : 'transparent',
		cursor: 'pointer'
	}),
	multiValue: (base) => ({
		...base,
		backgroundColor: '#eef2ff',
		borderRadius: 9999,
		paddingLeft: 4,
		paddingRight: 2
	}),
	multiValueLabel: (base) => ({
		...base,
		color: '#4338ca',
		fontSize: 12,
		fontWeight: 500
	}),
	multiValueRemove: (base) => ({
		...base,
		color: '#4338ca',
		borderRadius: 9999,
		':hover': { backgroundColor: '#c7d2fe', color: '#312e81' }
	})
};

export default LocationFilterModal;
